import { nextTick } from 'vue';
import {
  CONFIGURED_WORKFLOW_URL,
  WORKFLOW_ENTITIES_FOR_ASSIGNMENT_URL,
  STEP_ASSIGNED_SIGNERS_URL,
  TEMPLATES_ASSIGNED_TO_ENTITY_TYPE_URL,
  PREVIEW_DOCUSEAL_URL,
  SEND_CONTRACT,
  RESEND_DOCUSEAL_EMAIL_URL,
  TRACK_DOCUSEAL_STATUS_URL,
  DOWNLOAD_SIGNED_DOCUMENT_URL,
  get_headers,
  get_user_role,
} from '@/utils/constants/constants.js';
import axios from 'axios';
import formatting_mixins from '@/utils/mixins/formatting_mixins';
import alert_mixin from '@/utils/mixins/alert_mixin';
import e_signing_mixins from '@/utils/mixins/e_signing_mixins';

export default {
  name: 'SigningFlow',

  mixins: [alert_mixin, formatting_mixins, e_signing_mixins],

  data: () => ({
    headers: get_headers(),
    role: get_user_role(),

    // template selection
    assigned_templates: [],
    configured_templates: [],
    selected_template_id: null,

    active_template_request_id: 0,

    step_signers_request_seq: 0,

    // timeline / steps
    steps: [],
    local_steps: [],
    step_element_refs: [],
    node_top_offsets: [],
    resize_observer: null,
    selected_template: null,

    // CACHING
    template_steps_map: [],
    template_status_map: [],

    // preview
    preview_url: null,
    is_preview_loading: false,

    // manage modal
    show_manage_modal: false,
    current_step: null,
    manage_form: {
      name: '',
      surname: '',
      email: '',
    },

    // context
    project_id: Number(sessionStorage.getItem('project_id')) || null,
    entity_type_id: null,
    entity_profile_id: null,

    // table data for assign modal
    assignable_user_rows: [],
    assign_users_table_key: 0,

    // ids for payloads
    workflow_id: null,
    template_id: null,
    workflow_assignment_id: null,
  }),

  mounted() {
    if (this.$route.query.entity_type_id) {
      this.entity_type_id = Number(this.$route.query.entity_type_id);
    } else {
      this.entity_type_id = Number(sessionStorage.getItem('entity_type_id')) || null;
    }

    if (this.$route.query.entity_profile_id) {
      this.entity_profile_id = Number(this.$route.query.entity_profile_id);
    } else {
      this.entity_profile_id = Number(sessionStorage.getItem('entity_profile_id')) || null;
    }

    this.local_steps = JSON.parse(JSON.stringify(this.steps));

    nextTick(() => {
      this.measure_nodes();
      window.addEventListener('resize', this.handle_resize);

      if ('ResizeObserver' in window) {
        this.resize_observer = new ResizeObserver(this.measure_nodes);
        this.step_element_refs.forEach(element => {
          if (element) {
            this.resize_observer.observe(element);
          }
        });
      }
    });

    this.load_assigned_templates();
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.handle_resize);
    if (this.resize_observer) {
      this.resize_observer.disconnect();
    }
  },

  methods: {
    reset_steps() {
      this.steps = [];
      this.local_steps = [];
      this.step_element_refs = [];
      this.node_top_offsets = [];
    },

    find_current_step_index(workflow_steps, entity_type_id) {
      if (!entity_type_id) {
        return 0;
      }

      const found_index = workflow_steps.findIndex(
        workflow_step =>
          workflow_step.entity_type &&
          Number(workflow_step.entity_type.id) === Number(entity_type_id),
      );

      return found_index !== -1 ? found_index : 0;
    },

    safe_key(raw_key) {
      if (raw_key === null || raw_key === undefined) return null;

      // Only allow string/number keys
      if (typeof raw_key !== 'string' && typeof raw_key !== 'number') return null;

      const key = String(raw_key).trim();

      // Block prototype pollution keys
      if (key === '__proto__' || key === 'prototype' || key === 'constructor') {
        return null;
      }

      return key;
    },


    build_steps_from_workflow(template_obj, entity_type_id) {
      const workflow = template_obj.workflow || {};
      const workflow_steps = workflow.steps || [];

      this.template_id = template_obj.id || null;
      this.workflow_id = workflow.id || null;
      this.workflow_assignment_id = template_obj.assignment_id || null;

      const current_step_index = this.find_current_step_index(
        workflow_steps,
        entity_type_id,
      );

      this.steps = workflow_steps.map((workflow_step, index) => ({
        id: workflow_step.id,
        title:
          workflow_step.entity_type?.entity_type_name ||
          `Step ${index + 1}`,
        type_id: workflow_step.entity_type?.id,
        state: index === current_step_index ? 'current' : 'upcoming',
        owner: '',
        email: '',
        signers: [],
        signers_meta: [],
        expanded: false,
      }));

      this.local_steps = JSON.parse(JSON.stringify(this.steps));
      this.step_element_refs = [];
    },

    map_signers_by_step_id(api_steps) {
      const signers_by_step_id = {};

      api_steps.forEach(step_from_api => {
        const step_signers = step_from_api.signers || [];

        signers_by_step_id[step_from_api.id] = step_signers.map(signer_from_api => {
          const raw_status =
            signer_from_api.docuseal_status ??
            signer_from_api.display_status ??
            signer_from_api.status_label ??
            'awaiting';

          const normalised_status = this.normalise_status(raw_status);

          return {
            id: signer_from_api.signer_id || signer_from_api.id,
            name: signer_from_api.full_name || signer_from_api.email,
            email: signer_from_api.email,

            //UI display
            docuseal_status_label: normalised_status,

            //logic & computation
            status_label: normalised_status,

            signing_order: signer_from_api.signing_order,
          };
        });
      });

      return signers_by_step_id;
    },

    collect_assignable_users(entity_results) {
      const users_map = new Map();

      entity_results.forEach(entity_item => {
        (entity_item.users || []).forEach(user_item => {
          if (!user_item.user_id || users_map.has(user_item.user_id)) {
            return;
          }
          users_map.set(user_item.user_id, {
            id: user_item.user_id,
            first_name: user_item.first_name || '',
            last_name: user_item.last_name || '',
            email: user_item.email || '',
            is_assigned: false,
            is_loading: false,
          });
        });

        (entity_item.children || []).forEach(child_entity => {
          (child_entity.users || []).forEach(user_item => {
            if (!user_item.user_id || users_map.has(user_item.user_id)) {
              return;
            }
            users_map.set(user_item.user_id, {
              id: user_item.user_id,
              first_name: user_item.first_name || '',
              last_name: user_item.last_name || '',
              email: user_item.email || '',
              is_assigned: false,
              is_loading: false,
            });
          });
        });
      });

      return Array.from(users_map.values());
    },

    apply_assigned_signers(assignable_users, assigned_data) {
      if (assigned_data.status !== 'success') {
        this.show_error(
          assigned_data.message ||
            'Error loading assigned signers for step',
        );
        return assignable_users;
      }

      const signer_rows = Array.isArray(assigned_data.data)
        ? assigned_data.data
        : [];

      const assigned_user_ids = signer_rows
        .map(signer_item => signer_item.user_id)
        .filter(id => id != null)
        .map(Number);

      const assigned_user_id_set = new Set(assigned_user_ids);

      return assignable_users.map(user_row => ({
        ...user_row,
        is_assigned: assigned_user_id_set.has(Number(user_row.id)),
      }));
    },

    toggle_expand(step_item) {
      step_item.expanded = !step_item.expanded;
      nextTick(this.measure_nodes);
    },

    handle_resize() {
      this.measure_nodes();
    },

    measure_nodes() {
      const timeline_element = this.$el.querySelector('.signing-timeline');
      if (!timeline_element) return;

      const timeline_top =
        timeline_element.getBoundingClientRect().top + window.scrollY;

      this.node_top_offsets = this.step_element_refs.map(element => {
        if (!element) return 0;
        const element_top =
          element.getBoundingClientRect().top + window.scrollY;
        return Math.max(0, Math.round(element_top - timeline_top - 14));
      });
    },

    async load_assigned_templates() {
      if (!this.entity_type_id) return;

      try {
        const { data } = await axios.get(
          TEMPLATES_ASSIGNED_TO_ENTITY_TYPE_URL,
          {
            headers: this.headers,
            params: {
              project_id: this.project_id,
              entity_type_id: this.entity_type_id,
              entity_profile_id: this.entity_profile_id,
              is_assigned: true,
            },
          }
        );

        if (data.status !== 'success') {
          this.show_error(data.message || 'Failed to load templates');
          return;
        }

        this.assigned_templates = (data.results || []).map(template => {
          const status = template.submission_status ?? null;

          if (status) {
            this.template_status_map[template.template_id] =
              this.normalise_status(status);
          }

          return {
            ...template,
            submission_status: status,
          };
        });
      } catch (err) {
        this.show_error(err.message);
      }
    },

    load_configured_workflow_for_template(template_id) {
      if (!this.entity_type_id || !template_id) {
        return Promise.resolve(null);
      }

      return axios
        .get(CONFIGURED_WORKFLOW_URL, {
          headers: this.headers,
          params: {
            project_id: this.project_id,
            entity_type_id: this.entity_type_id,
            template_id,
          },
        })
        .then(response => {
          const response_data = response.data || {};

          if (response_data.status !== 'success') {
            this.show_error(
              response_data.message ||
                'Error during retrieval of configured workflow',
            );
            this.reset_steps();
            return null;
          }

          const items = Array.isArray(response_data.data)
            ? response_data.data
            : [];

          if (items.length === 0) {
            this.show_error(
              'No configured workflow found for this template. Please assign a workflow first.',
            );
            this.reset_steps();
            return null;
          }

          const template_obj = items[0] || {};
          this.build_steps_from_workflow(
            template_obj,
            this.entity_type_id,
          );

          nextTick(this.measure_nodes);

          return this.load_step_signers({ silent: true });
        })
        .catch(error => {
          this.show_error(
            'Error during retrieval of configured workflow: ' +
              (error.response?.data?.message || error.message),
          );
          return null;
        });
    },

   async handle_template_click(template_row) {
  const template_id = template_row?.template_id;

  if (!template_id) {
    this.show_error("Missing template_id on template row.");
    return;
  }

  // IMPORTANT: use safe key ONCE and re-use
  const safe_template_id = this.safe_key(template_id);
  if (!safe_template_id) {
    this.show_error("Invalid template_id.");
    return;
  }

  // mark this as the latest click/request (race condition guard)
  const request_id = ++this.active_template_request_id;

  // set selection immediately (UI highlight, open panel etc)
  this.selected_template_id = template_id;
  this.selected_template = template_row;

  // always keep these in sync (used by other calls)
  this.workflow_assignment_id =
    template_row.workflow_assignment_id || template_row.assignment_id || null;
  this.workflow_id = template_row.workflow_id || null;

  // 1) Refresh overall submission status (DocuSeal source of truth)
  if (template_row.submission_id) {
    try {
      const res = await axios.post(
        TRACK_DOCUSEAL_STATUS_URL,
        { submission_id: template_row.submission_id },
        { headers: this.headers },
      );

      // if a newer click happened, stop
      if (request_id !== this.active_template_request_id) return;

      const overall = res?.data?.overall_status;

      if (overall) {
        template_row.submission_status = overall;
        Reflect.set(this.template_status_map, safe_template_id, overall);
      }
    } catch (error) {
      // don't break UX if status refresh fails
      console.warn("Failed to refresh DocuSeal status:", error);
    }
  }

  // 2) If we have cached steps, use them and stop (fast path)
  const cached_steps = Reflect.get(this.template_steps_map, safe_template_id);

  if (cached_steps) {
    // Deep clone to avoid flicker/reactivity mutation
    this.local_steps = structuredClone(cached_steps);
    // fallback if structuredClone isn't available:

    await nextTick();

    // if a newer click happened, stop
    if (request_id !== this.active_template_request_id) return;

    this.measure_nodes();
    return;
  }

  // 3) Load workflow + steps (this will create empty steps)
  await this.load_configured_workflow_for_template(template_id);

  // if a newer click happened, stop
  if (request_id !== this.active_template_request_id) return;

  // 4) Load signer statuses for each step (fills signers_meta)
  if (this.workflow_assignment_id && this.entity_profile_id) {
    await this.load_step_signers({ silent: true, request_id });

    // if a newer click happened, stop
    if (request_id !== this.active_template_request_id) return;

    // 5) Cache FINAL steps (with signers_meta) so it won't flicker later
    Reflect.set(
      this.template_steps_map,
      safe_template_id,
      structuredClone(this.local_steps),
    );
    // fallback:
  }

  // 6) Re-measure timeline positions once DOM updated
  await nextTick();

  // if a newer click happened, stop
  if (request_id !== this.active_template_request_id) return;

  this.measure_nodes();
},


    async fetch_assignable_users(entity_type_id_for_step) {
      const entity_type_id = entity_type_id_for_step;
      const entity_profile_id = this.entity_profile_id;

      try {
        const entities_response = await axios.get(
          WORKFLOW_ENTITIES_FOR_ASSIGNMENT_URL,
          {
            headers: this.headers,
            params: {
              entity_type_id,
              entity_profile_id,
            },
          },
        );

        const entities_payload = entities_response.data || {};

        if (entities_payload.status !== 'success') {
          this.show_error('Error retrieving entities for assignment');
          return;
        }

        const entity_results = entities_payload.results || [];
        let assignable_users = this.collect_assignable_users(entity_results);

        if (this.template_id && this.workflow_id && this.current_step) {
          const assigned_response = await axios.get(
            STEP_ASSIGNED_SIGNERS_URL,
            {
              headers: this.headers,
              params: {
                workflow_assignment_id: this.workflow_assignment_id,
                step_id: this.current_step.id,
                entity_profile_id: this.entity_profile_id,
              },
            },
          );

          const assigned_data = assigned_response.data || {};
          assignable_users = this.apply_assigned_signers(
            assignable_users,
            assigned_data,
          );
        }

        this.assignable_user_rows = assignable_users;
        this.assign_users_table_key += 1;
      } catch (error) {
        this.show_error(
          'Error loading assignable users: ' +
            (error.response?.data?.message || error.message),
        );
      }
    },

    async open_manage(step_item) {
      if (this.role?.includes('Entity')) {
        return;
      }
      this.current_step = step_item;

      const full_owner_name = step_item.owner || '';
      const owner_name_parts = full_owner_name.trim().split(' ');

      this.manage_form.name = owner_name_parts[0] || '';
      this.manage_form.surname = owner_name_parts.slice(1).join(' ') || '';
      this.manage_form.email = step_item.email || '';

      const entity_type_id_for_step = step_item.type_id;
      await this.fetch_assignable_users(entity_type_id_for_step);
    },

    close_manage() {
      this.show_manage_modal = false;
      this.current_step = null;
    },

    preview_template(template) {
      this.selected_template = template;
      this.is_preview_loading = true;
      this.preview_url = null;

      const request_payload = {
        template_id: template.template_id,
        entity_id: this.entity_profile_id
      };

      axios.post(PREVIEW_DOCUSEAL_URL, request_payload, { headers: this.headers })
        .then(response => {
          const payload = response.data;

          if (payload.status !== "success") {
            this.show_error(payload.message || "Failed to generate preview");
            return;
          }

          this.preview_url = payload.embed_src;
        })
        .catch(error => {
          const error_message = error.response?.data?.message || error.message;
          this.show_error(error_message);
        })
        .finally(() => {
          this.is_preview_loading = false;
        });
    },

    confirm_send_contract(template) {
      this.show_confirm(
        "Are you sure you want to send this contract?",
        "This will send the contract to all signers.",
        () => {
          this.send_contract(template);
        },
        () => {
          // Cancel
        }
      );
    },

    send_contract(template) {
      const workflow_assignment_id = template.id;

      if (!workflow_assignment_id) {
        this.show_error("Missing workflow assignment ID for this template.");
        return;
      }

      if (!this.entity_profile_id) {
        this.show_error("Missing entity profile ID.");
        return;
      }

      const payload = {
        workflow_assignment_id: workflow_assignment_id,
        entity_profile_id: this.entity_profile_id,
        send_email: true,
      };

      axios.post(SEND_CONTRACT, payload, { headers: this.headers })
        .then(res => {
          if (res.data.status !== "success") {
            this.show_error(res.data.message || "Failed to send contract");
            return;
          }

          this.show_success("Contract sent successfully!");
          this.close_modal("preview_template_modal");

          // Invalidate cache after sending (Codacy-safe)
          const safe_template_id = this.safe_key(this.selected_template_id);
          if (safe_template_id) {
            Reflect.set(this.template_steps_map, safe_template_id, undefined);
          }
        })
        .catch(err => {
          const msg =
            err.response?.data?.details ||
            err.response?.data?.message ||
            err.message;
          this.show_error(msg);
        });
      },

    normalise_status(raw_status) {
      if (!raw_status) return '';
      return String(raw_status).toLowerCase().trim();
    },

    compute_status_from_signers(signers_meta) {
      const signer_statuses = (signers_meta || [])
        .map(signer => this.normalise_status(signer.status_label))
        .filter(Boolean);

      if (!signer_statuses.length) return 'awaiting';

      if (signer_statuses.some(status => status === 'declined')) return 'declined';
      if (signer_statuses.every(status => status === 'completed')) return 'completed';
      if (signer_statuses.some(status => status === 'sent' || status === 'opened')) return 'pending';

      return 'awaiting';
    },

    get_step_overall_status(step) {
      // 1) Step-specific signers are the source of truth
      if (Array.isArray(step?.signers_meta) && step.signers_meta.length > 0) {
        return this.compute_status_from_signers(step.signers_meta);
      }

      // 2) Step-level status if backend provides it
      if (step?.status) {
        return this.normalise_status(step.status);
      }

      // 3) If there are no signers, DO NOT use template submission status
      return 'awaiting';
    },

    get_step_primary_name(step) {
      if (!step?.signers_meta?.length) return null;

      if (step.signers_meta.length === 1) {
        return step.signers_meta[0].name;
      }

      return `${step.signers_meta[0].name} +${step.signers_meta.length - 1}`;
    },



    get_step_status_icon_class(status) {
      const normalised_status = this.normalise_status(status);

      if (normalised_status === 'completed') return 'bi bi-check-circle';
      if (normalised_status === 'declined') return 'bi bi-x-circle';

      return 'bi bi-clock-history';
    },

    get_template_status_label(template) {
      if (this.template_status_map[template.template_id]) {
        const value = String(this.template_status_map[template.template_id]);
        return value.charAt(0).toUpperCase() + value.slice(1);
      }

      if (template && template.submission_status) {
        const value = String(template.submission_status);
        return value.charAt(0).toUpperCase() + value.slice(1);
      }

      if (this.local_steps.length && this.selected_template_id === template.template_id) {
        const step_statuses = this.local_steps.map(step =>
          this.get_step_overall_status(step)
        );

        if (step_statuses.some(status => status === 'declined')) return 'Declined';
        if (step_statuses.every(status => status === 'completed')) return 'Completed';
      }

      return 'Pending';
    },

    get_submitter_icon_class(status) {
      const normalised_status = this.normalise_status(status);

      if (normalised_status === 'completed') return 'bi bi-check-circle text-success me-2';
      if (normalised_status === 'opened') return 'bi bi-clock-history text-primary me-2';
      if (normalised_status === 'sent') return 'bi bi-send text-primary me-2';
      if (normalised_status === 'awaiting') return 'bi bi-clock text-muted me-2';
      if (normalised_status === 'declined') return 'bi bi-x-circle text-danger me-2';

      return 'bi bi-clock text-muted me-2';
    },

    get_submitter_badge_class(status) {
      const normalised_status = this.normalise_status(status);

      if (normalised_status === 'completed') return 'signing-status-completed';
      if (normalised_status === 'sent') return 'signing-status-sent';
      if (normalised_status === 'opened') return 'signing-status-opened';
      if (normalised_status === 'awaiting') return 'signing-status-awaiting';
      if (normalised_status === 'declined') return 'signing-status-declined';

      return 'signing-status-awaiting';
    },

    format_status_label(status) {
      if (!status || status === 'undefined' || status === 'null') {
        return 'Awaiting';
      }

      const normalised_status = this.normalise_status(status);

      const status_map = Object.freeze(Object.assign(Object.create(null), {
        completed: 'Completed',
        pending: 'In Progress',
        awaiting: 'Awaiting',
        sent: 'Sent',
        opened: 'Viewed',
        declined: 'Declined',
        signed: 'Signed',
      }));

      const label = Reflect.get(status_map, normalised_status);

      return label ||
        normalised_status.charAt(0).toUpperCase() + normalised_status.slice(1);
    },


    extract_signer_status(raw_signer) {
      if (!raw_signer) return 'awaiting';

      const raw =
        raw_signer.status ??
        raw_signer.docuseal_status ??
        raw_signer.status_label ??
        '';

      if (!raw) return 'awaiting';
      return String(raw).toLowerCase().trim();
    },

    async refresh_step_signers() {
      if (!this.workflow_assignment_id || !this.entity_profile_id) {
        return;
      }

      await this.load_step_signers({ silent: true });

      // Update cache after refresh
      if (this.selected_template_id) {
        this.template_steps_map[this.selected_template_id] =
          JSON.parse(JSON.stringify(this.local_steps));
      }
    },

    resend_submitter_email(signer) {
      if (!signer || !signer.id) {
        this.show_error("Invalid signer selected.");
        return;
      }

      axios.post(
        RESEND_DOCUSEAL_EMAIL_URL,
        { signer_id: signer.id },
        { headers: this.headers }
      )
      .then((response) => {
        const payload = response.data;

        if (payload.status !== "success") {
          this.show_error(
            this.normalise_resend_error(payload.message || payload.error)
          );
          return;
        }

        this.show_success("Signing email resent successfully!");
        return this.refresh_step_signers();
      })
      .catch((error) => {
        const raw =
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.response?.data?.details ||
          error.message;

        this.show_error(this.normalise_resend_error(raw));
      });
    },

    normalise_resend_error(msg) {
      if (!msg) return "Failed to resend email.";

      const text = msg.toLowerCase();

      if (text.includes("already completed")) {
        return "This signer has already completed signing.";
      }

      if (text.includes("submitter") && text.includes("not found")) {
        return "The signing request for this signer could not be found.";
      }

      if (text.includes("email")) {
        return "Unable to resend email. Please check the signer's email address.";
      }

      if (text.includes("docuseal")) {
        return "DocuSeal returned an unexpected response. Please try again.";
      }

      return msg;
    },

    download_signed_document(submission_id) {
  if (!submission_id) {
    this.show_error("Missing submission ID");
    return;
  }

  axios.get(DOWNLOAD_SIGNED_DOCUMENT_URL, {
    params: { submission_id },
    headers: this.headers,
  })
  .then(res => {
    const payload = res.data || {};

    if (payload.status !== "success") {
      this.show_error(payload.message || "Failed to prepare download");
      return;
    }

    const presigned_url = payload?.data?.presigned_url;
    const filename = payload?.data?.filename || "signed_document.pdf";

    if (!presigned_url) {
      this.show_error("Download link not available");
      return;
    }

    // triggers download without leaving SPA
    const link = document.createElement("a");
    link.href = presigned_url;
    link.setAttribute("download", filename); // may be ignored, but still downloads
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    link.remove();

    this.show_success("Downloading signed document...");
  })
  .catch(error => {
    const msg =
      error.response?.data?.message ||
      error.response?.data?.details ||
      error.message ||
      "Failed to download signed PDF";
    this.show_error(msg);
  });
},


  },
};